"use client";

import { DragDropContext } from "@hello-pangea/dnd";
import type { Signal } from "@preact/signals-react";
import { useSignal } from "@preact/signals-react";
import { useSignals } from "@preact/signals-react/runtime";
import type { EditorView } from "prosemirror-view";
import type {
  Dispatch,
  DragEvent as ReactDragEvent,
  ReactNode,
  Ref,
  SetStateAction,
} from "react";
import {
  createContext,
  use,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useLocalStorage } from "usehooks-ts";

import type { PollFormValues } from "@/components/app/editor-v3/editor-poll-form";
import type { EmbedURLMetadata } from "@/components/app/post/embed-player";
import { useDetectEmbedURLs } from "@/components/app/post/embed-player";
import { useIsClient } from "@/hooks/use-is-client";
import { LocalStorageKeys } from "@/utils/local-storage-keys";
import type { ImageMetadata, MediaItem } from "@/utils/media-utils";
import { isMediaUploading } from "@/utils/media-utils";
import type { TierSchema } from "@/utils/subscriptions";

import type { EDITOR_MEDIA_TAB } from "./constants";
import { EDITOR_MODE } from "./constants";
import {
  GATED_CONTENT_REQUIRED_MESSAGE,
  hasRequiredGatedPayload,
} from "./editor-gated-content-utils";
import {
  docHasUnresolvedInlineImageUpload,
  markdownHasLocalBlobImage,
} from "./inline-image-upload-state";
import { serializeDocToMarkdown } from "./markdown/markdown";
import { serializeMarkdownSourceDoc } from "./markdown/source-doc";

type EditorTypes = {
  prosemirror: EditorView;
};

type ImmediateTextField = "body" | "gatedBody";

export type DataFields = Pick<
  EditorContextType,
  "body" | "gatedBody" | "media" | "gatedMedia" | "poll"
>;

export type EditorContextType = {
  activeMediaTab: EDITOR_MEDIA_TAB | null;
  autoFocus: boolean;
  body: string;
  detectedURLs: EmbedURLMetadata[];
  disabledURLs: string[];
  editorInstances: Record<string, () => EditorView>;
  empty: () => boolean;
  errorMessages: Record<keyof DataFields, Set<string>>;
  exposed: Record<string, unknown>;
  focused: () => boolean;
  focusedField: keyof DataFields | null;
  gated: () => boolean;
  gatedBody: string;
  gatedMedia: MediaItem[];
  getDataAttributes: () => Record<string, string | boolean | number | null>;
  getEditorInstance<T extends keyof EditorTypes>(
    type: T,
    name: string,
  ): EditorTypes[T];
  hasImmediateTextContent: (field: ImmediateTextField) => boolean;
  immediateTextContent: Record<ImmediateTextField, boolean>;
  inlineImageUploading: Record<ImmediateTextField, boolean>;
  inlineImageMetadata: Record<string, ImageMetadata>;
  lastFocusedField: keyof DataFields | null;
  media: MediaItem[];
  mediaUploading: boolean;
  mode: EDITOR_MODE;
  onChange: (editorContext: EditorContextType) => void;
  onSubmit: (editorContext: EditorContextType) => void;
  placeholder: string;
  poll?: PollFormValues;
  reset: () => void;
  setEditorContext: Dispatch<SetStateAction<Partial<EditorContextType>>>;
  setEditorInstance: (i: {
    getter: () => EditorView;
    name: string;
    type: string;
  }) => void;
  setImmediateTextContent: (
    field: ImmediateTextField,
    hasContent: boolean,
  ) => void;
  setInlineImageUploading: (
    field: ImmediateTextField,
    uploading: boolean,
  ) => void;
  signal: Signal<EditorContextType>;
  submitting: boolean;
  subscriptionTier: TierSchema | null;
  syncLiveTextFields: () => EditorContextType;
  uploading: () => boolean;
};

const EditorContext = createContext<EditorContextType>(null!);

type EditorFileDropContextType = {
  clearFileDragState: () => void;
  enableFileDrop: boolean;
  isFileDragActive: boolean;
};

const EditorFileDropContext = createContext<EditorFileDropContextType>({
  clearFileDragState: () => undefined,
  enableFileDrop: false,
  isFileDragActive: false,
});

type EditorContextProviderProps = Partial<EditorContextType> & {
  children: ReactNode;
  enableFileDrop?: boolean;
  onChange?: (editorContext: EditorContextType) => void;
  onSubmit: (editorContext: EditorContextType) => void;
  ref?: Ref<EditorContextType>;
};

const IMMEDIATE_TEXT_FIELDS: ImmediateTextField[] = ["body", "gatedBody"];

const hasOwnKey = <T extends object>(obj: T, key: PropertyKey) =>
  Object.prototype.hasOwnProperty.call(obj, key);

const isFileDrag = (dataTransfer?: DataTransfer | null) =>
  Array.from(dataTransfer?.types ?? []).includes("Files");

const syncImmediateTextContentFromFields = (
  state: EditorContextType,
  newState: Partial<EditorContextType>,
) => {
  let nextImmediateTextContent = state.immediateTextContent;

  for (const field of IMMEDIATE_TEXT_FIELDS) {
    if (!hasOwnKey(newState, field)) {
      continue;
    }

    const nextHasContent = Boolean(newState[field]);

    if (nextImmediateTextContent[field] === nextHasContent) {
      continue;
    }

    if (nextImmediateTextContent === state.immediateTextContent) {
      nextImmediateTextContent = { ...state.immediateTextContent };
    }

    nextImmediateTextContent[field] = nextHasContent;
  }

  return nextImmediateTextContent;
};

const readLiveEditorText = (
  state: EditorContextType,
  field: ImmediateTextField,
) => {
  const getter = state.editorInstances[`prosemirror${field}`];

  if (!getter) {
    return null;
  }

  try {
    return state.mode === EDITOR_MODE.SIMPLE
      ? serializeMarkdownSourceDoc(getter().state.doc)
      : serializeDocToMarkdown(getter().state.doc);
  } catch {
    return null;
  }
};

const syncLiveTextFieldsIntoState = (state: EditorContextType) => {
  let nextState = state;

  for (const field of IMMEDIATE_TEXT_FIELDS) {
    const liveValue = readLiveEditorText(nextState, field);

    if (liveValue == null || liveValue === nextState[field]) {
      continue;
    }

    if (nextState === state) {
      nextState = {
        ...state,
        immediateTextContent: { ...state.immediateTextContent },
      };
    }

    nextState[field] = liveValue;
    nextState.immediateTextContent[field] = liveValue.length > 0;
  }

  return nextState;
};

const editorHasPendingInlineImageUpload = (
  getter: (() => EditorView) | null,
) => {
  if (!getter) {
    return false;
  }

  try {
    return docHasUnresolvedInlineImageUpload(getter().state.doc);
  } catch {
    return false;
  }
};

const hasPendingInlineImageUploads = (state: EditorContextType) =>
  IMMEDIATE_TEXT_FIELDS.some((field) => {
    if (
      editorHasPendingInlineImageUpload(
        state.editorInstances[`prosemirror${field}`] ?? null,
      )
    ) {
      return true;
    }

    const liveValue = readLiveEditorText(state, field);

    return liveValue ? markdownHasLocalBlobImage(liveValue) : false;
  });

export const EditorContextProvider = ({
  children,
  enableFileDrop = false,
  ref,
  onSubmit,
  onChange,
  ...props
}: EditorContextProviderProps) => {
  useSignals();
  const isClient = useIsClient();
  const eventsRef = useRef({ onChange, onSubmit });
  eventsRef.current = { onChange, onSubmit };
  const [isFileDragActive, setIsFileDragActive] = useState(false);
  const [mode, setMode] = useLocalStorage(
    LocalStorageKeys.editorMode,
    EDITOR_MODE.SIMPLE,
  );

  const clearFileDragState = useCallback(() => {
    setIsFileDragActive(false);
  }, []);

  const defaultState = {
    activeMediaTab: null,
    autoFocus: false,
    body: "",
    detectedURLs: [],
    disabledURLs: [],
    editorInstances: {},
    errorMessages: {
      body: new Set<string>(),
      gatedBody: new Set<string>(),
      gatedMedia: new Set<string>(),
      media: new Set<string>(),
      poll: new Set<string>(),
    },
    exposed: {},
    focusedField: null,
    gatedBody: "",
    gatedMedia: [],
    immediateTextContent: {
      body: Boolean(props.body),
      gatedBody: Boolean(props.gatedBody),
    },
    inlineImageUploading: {
      body: false,
      gatedBody: false,
    },
    inlineImageMetadata: {},
    lastFocusedField: null,
    media: [],
    mediaUploading: false,
    mode,
    placeholder: "",
    poll: undefined,
    submitting: false,
    subscriptionTier: null,

    onChange(c: EditorContextType) {
      eventsRef.current.onChange?.(c);
    },
    onSubmit(c: EditorContextType) {
      const syncedContext = c.syncLiveTextFields();

      if (syncedContext.uploading()) {
        return;
      }

      eventsRef.current.onSubmit(syncedContext);
    },

    ...props,
  } as Omit<
    EditorContextType,
    | "empty"
    | "focused"
    | "gated"
    | "getDataAttributes"
    | "getEditorInstance"
    | "hasImmediateTextContent"
    | "reset"
    | "setEditorContext"
    | "setEditorInstance"
    | "setImmediateTextContent"
    | "setInlineImageUploading"
    | "signal"
    | "syncLiveTextFields"
    | "uploading"
  >;

  const signal: Signal<EditorContextType> = useSignal<EditorContextType>({
    ...defaultState,

    empty() {
      return (
        !signal.value.immediateTextContent.body &&
        !signal.value.immediateTextContent.gatedBody &&
        signal.value.media.length === 0 &&
        signal.value.gatedMedia.length === 0 &&
        !signal.value.poll
      );
    },
    focused() {
      return signal.value.focusedField !== null;
    },
    gated() {
      return Boolean(signal.value.subscriptionTier);
    },
    get signal() {
      return signal;
    },
    getDataAttributes() {
      return {
        "data-empty": signal.value.empty(),
        "data-focused": signal.value.focused(),
        "data-gated": signal.value.gated(),
        "data-media-tab": signal.value.activeMediaTab,
        "data-mode": signal.value.mode,
        "data-not-empty": !signal.value.empty(),
        "data-not-empty-or-focused":
          !signal.value.empty() || signal.value.focused(),
      };
    },
    getEditorInstance<T extends keyof EditorTypes>(
      type: T,
      name: string,
    ): EditorTypes[T] {
      return signal.value.editorInstances[type + name]() as EditorTypes[T];
    },
    hasImmediateTextContent(field) {
      return signal.value.immediateTextContent[field];
    },
    reset() {
      signal.value.setEditorContext(defaultState);
    },
    setEditorContext(newState) {
      if (typeof newState === "function") {
        newState = newState(signal.value);
      }

      const nextState = { ...signal.value, ...newState };
      nextState.immediateTextContent = syncImmediateTextContentFromFields(
        signal.value,
        newState,
      );

      if (
        (!nextState.subscriptionTier || hasRequiredGatedPayload(nextState)) &&
        nextState.errorMessages.gatedBody.has(GATED_CONTENT_REQUIRED_MESSAGE)
      ) {
        nextState.errorMessages = {
          ...nextState.errorMessages,
          gatedBody: new Set(nextState.errorMessages.gatedBody),
        };
        nextState.errorMessages.gatedBody.delete(
          GATED_CONTENT_REQUIRED_MESSAGE,
        );
      }

      signal.value = nextState;
    },
    setEditorInstance: ({ getter, name, type }) => {
      signal.value.editorInstances[type + name] = getter;
    },
    setImmediateTextContent(field, hasContent) {
      if (signal.value.immediateTextContent[field] === hasContent) {
        return;
      }

      signal.value = {
        ...signal.value,
        immediateTextContent: {
          ...signal.value.immediateTextContent,
          [field]: hasContent,
        },
      };
    },
    setInlineImageUploading(field, uploading) {
      if (signal.value.inlineImageUploading[field] === uploading) {
        return;
      }

      signal.value = {
        ...signal.value,
        inlineImageUploading: {
          ...signal.value.inlineImageUploading,
          [field]: uploading,
        },
      };
    },
    syncLiveTextFields() {
      const nextState = syncLiveTextFieldsIntoState(signal.value);

      if (nextState !== signal.value) {
        signal.value = nextState;
      }

      return nextState;
    },
    uploading() {
      return (
        signal.value.media.some(isMediaUploading) ||
        signal.value.gatedMedia.some(isMediaUploading) ||
        Object.values(signal.value.inlineImageUploading).some(Boolean) ||
        hasPendingInlineImageUploads(signal.value)
      );
    },
  });

  useImperativeHandle(ref, () => signal.value, [signal.value]);

  const detectedEmbedURLs = useDetectEmbedURLs({
    cache: false,
    text: [signal.value.body, signal.value.gatedBody],
  });
  const detectedEmbedURLsKey = JSON.stringify(detectedEmbedURLs);

  useEffect(() => {
    if (detectedEmbedURLsKey === JSON.stringify(signal.value.detectedURLs)) {
      return;
    }

    signal.value.setEditorContext({
      detectedURLs: detectedEmbedURLs,
    });
  }, [detectedEmbedURLs, detectedEmbedURLsKey, signal]);

  useEffect(() => {
    signal.value.onChange?.(signal.value);
  }, [
    signal.value.body,
    signal.value.gatedBody,
    signal.value.media,
    signal.value.gatedMedia,
    signal.value.poll,
    signal.value.mode,
  ]);

  useEffect(() => {
    if (!props.mode) {
      setMode(signal.value.mode);
    }
  }, [props.mode, setMode, signal.value.mode]);

  useEffect(() => {
    if (!enableFileDrop) {
      clearFileDragState();
      return;
    }

    const activateFileDragState = (event: DragEvent) => {
      if (isFileDrag(event.dataTransfer)) {
        setIsFileDragActive(true);
      }
    };

    window.addEventListener("dragenter", activateFileDragState, true);
    window.addEventListener("dragover", activateFileDragState, true);
    window.addEventListener("drop", clearFileDragState, true);
    window.addEventListener("dragend", clearFileDragState, true);
    window.addEventListener("blur", clearFileDragState);

    return () => {
      window.removeEventListener("dragenter", activateFileDragState, true);
      window.removeEventListener("dragover", activateFileDragState, true);
      window.removeEventListener("drop", clearFileDragState, true);
      window.removeEventListener("dragend", clearFileDragState, true);
      window.removeEventListener("blur", clearFileDragState);
    };
  }, [clearFileDragState, enableFileDrop]);

  if (!isClient) {
    return null;
  }

  return (
    <EditorContext value={signal.value}>
      <EditorFileDropContext.Provider
        value={{
          clearFileDragState,
          enableFileDrop,
          isFileDragActive,
        }}
      >
        <DragDropContext
          onDragEnd={(result) => {
            const { source, destination } = result;

            if (!destination) {
              return;
            }

            const media = signal.value.media.slice();
            const gatedMedia = signal.value.gatedMedia.slice();
            const byZone = { gatedMedia, media };
            const item =
              byZone[source.droppableId as "media" | "gatedMedia"][
                source.index
              ];

            byZone[source.droppableId as "media" | "gatedMedia"].splice(
              source.index,
              1,
            );
            byZone[destination.droppableId as "media" | "gatedMedia"].splice(
              destination.index,
              0,
              item,
            );

            signal.value.setEditorContext(byZone);
          }}
        >
          <div
            {...signal.value.getDataAttributes()}
            className="size-full"
            data-editor-file-drop-root={enableFileDrop || undefined}
            onDropCapture={(event: ReactDragEvent<HTMLDivElement>) => {
              if (!enableFileDrop || !isFileDrag(event.dataTransfer)) {
                return;
              }

              if (
                event.target instanceof Element &&
                event.target.closest(
                  "[data-editor-file-drop-target='true'], [data-editor-container]",
                )
              ) {
                return;
              }

              event.preventDefault();
              event.stopPropagation();
              clearFileDragState();
            }}
          >
            {children}
          </div>
        </DragDropContext>
      </EditorFileDropContext.Provider>
    </EditorContext>
  );
};

export const useEditorContext = () => use(EditorContext);
export const useEditorFileDropContext = () => use(EditorFileDropContext);

type EditorFieldContextType = {
  name: keyof DataFields;
  placeholder?: string;
};

const EditorFieldContext = createContext<EditorFieldContextType>({
  name: "body",
});

export const EditorFieldContextProvider = ({
  children,
  name,
  placeholder,
}: {
  children: ReactNode;
} & EditorFieldContextType) => {
  return (
    <EditorFieldContext.Provider value={{ name, placeholder }}>
      {children}
    </EditorFieldContext.Provider>
  );
};

export const useEditorFieldContext = <T extends keyof DataFields>() => {
  const editorContext = useEditorContext();
  const fieldContext = use(EditorFieldContext);

  if (!fieldContext) {
    throw new Error(
      "useEditorFieldContext must be used within EditorFieldContextProvider",
    );
  }

  if (!editorContext) {
    throw new Error(
      "useEditorFieldContext must be used within EditorContextProvider",
    );
  }

  return {
    ...fieldContext,
    addErrors: (errors: string[]) => {
      errors.forEach((error) => {
        editorContext.errorMessages[fieldContext.name].add(error);
      });
      editorContext.setEditorContext({});
    },
    editorContext,
    errors: editorContext.errorMessages[fieldContext.name],
    getEditorInstance<TType extends keyof EditorTypes>(
      type: TType,
    ): EditorTypes[TType] {
      return editorContext.getEditorInstance(type, fieldContext.name as string);
    },
    hasImmediateTextContent:
      fieldContext.name === "body" || fieldContext.name === "gatedBody"
        ? editorContext.hasImmediateTextContent(fieldContext.name)
        : false,
    setEditorInstance({
      getter,
      type,
    }: {
      getter: () => EditorView;
      type: string;
    }) {
      editorContext.setEditorInstance({
        getter,
        name: fieldContext.name as string,
        type,
      });
    },
    setEditorValue: (value: SetStateAction<EditorContextType[T]>) => {
      if (typeof value === "function") {
        value = value(editorContext.signal.value[fieldContext.name as T]);
      }

      if (value === editorContext.signal.value[fieldContext.name as T]) {
        return;
      }

      editorContext.setEditorContext({ [fieldContext.name]: value });
    },
    setImmediateTextContent: (hasContent: boolean) => {
      if (fieldContext.name !== "body" && fieldContext.name !== "gatedBody") {
        return;
      }

      editorContext.setImmediateTextContent(fieldContext.name, hasContent);
    },
    setInlineImageUploading: (uploading: boolean) => {
      if (fieldContext.name !== "body" && fieldContext.name !== "gatedBody") {
        return;
      }

      editorContext.setInlineImageUploading(fieldContext.name, uploading);
    },
    value: editorContext[fieldContext.name] as EditorContextType[T],
  };
};
